'use client';

import { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CountryIso2 } from 'react-international-phone';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/nextjs';
import { CheckCircle as CheckCircleIcon, Gift as GiftIcon } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import { getAuthMnemonicFromError } from '@nutralab/api';
import { Button, FormGroup, Icon, Input, PasswordInput, PhoneInput, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './RegisterModal.module.scss';

import { AuthModalLayout } from '@/app/_modals/_components/AuthModalLayout';
import { LoginModal, type LoginModalData } from '@/app/_modals/LoginModal';
import { useAuth } from '@/app/_providers/AuthProvider';
import { useModal } from '@/app/_providers/ModalProvider';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { storefrontAuth } from '@/shared/api/browser';
import { ANALYTICS_GOALS } from '@/shared/constants';
import { ModalProps } from '@/shared/types';
import { passwordField, phoneField, reachGoal, stringRequired, translateAuthMessage } from '@/shared/utils';

const RESEND_TIMEOUT_SECONDS = 60;

type Step = 'form' | 'code' | 'success';

const createFormSchema = (
    t: ReturnType<typeof useTranslation>['t'],
): z.ZodObject<{
    phone: z.ZodEffects<z.ZodString, string, string>;
    password: z.ZodString;
    confirmPassword: z.ZodString;
}> =>
    z.object({
        phone: phoneField(t),
        password: passwordField(t),
        confirmPassword: stringRequired(t),
    });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const createCodeSchema = (
    t: ReturnType<typeof useTranslation>['t'],
): z.ZodObject<{
    code: z.ZodString;
}> =>
    z.object({
        code: stringRequired(t),
    });

type CodeValues = z.infer<ReturnType<typeof createCodeSchema>>;

function formatCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60)
        .toString()
        .padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
}

export interface RegisterModalData {
    referralCode?: string;
}

type RegisterModalProps = ModalProps<RegisterModalData>;

/** Registration: phone, then the SMS code and a password; a referral code from the link is sent along. */
export function RegisterModal({ onClose, zIndex, data }: RegisterModalProps): ReactElement {
    const { t, i18n } = useTranslation();
    const { country, region } = useSiteConfig();
    const { setSession } = useAuth();
    const { openModal } = useModal();

    const referralCode = data?.referralCode;

    const [step, setStep] = useState<Step>('form');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [isResending, setIsResending] = useState(false);

    const formSchema = useMemo(
        () =>
            createFormSchema(t).refine((data) => data.password === data.confirmPassword, {
                message: t('validation.password-mismatch'),
                path: ['confirmPassword'],
            }),
        [t],
    );
    const codeSchema = useMemo(() => createCodeSchema(t), [t]);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
    });

    const {
        register: registerCode,
        handleSubmit: handleCodeSubmit,
        formState: { errors: codeErrors, isSubmitting: isCodeSubmitting },
    } = useForm<CodeValues>({
        resolver: zodResolver(codeSchema),
        mode: 'onTouched',
    });

    useEffect(() => {
        if (step !== 'code') {
            return;
        }

        const id = setInterval(() => {
            setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        return (): void => clearInterval(id);
    }, [step]);

    const handleRegisterRequestError = (err: unknown): void => {
        const mnemonic = getAuthMnemonicFromError(err);

        switch (mnemonic?.message) {
            case 'auth.user_already_registered':
                toast.info(translateAuthMessage(t, mnemonic.message, 'auth.register.error'));
                handleOpenLogin();
                return;
            case 'auth.code_request_cooldown':
                toast.error(
                    translateAuthMessage(t, mnemonic.message, 'auth.register.error', {
                        seconds: mnemonic.secondsRemaining,
                    }),
                );
                setSecondsLeft(mnemonic.secondsRemaining ?? RESEND_TIMEOUT_SECONDS);
                return;
            default:
                if (!mnemonic || mnemonic.message === 'auth.internal_error') {
                    Sentry.captureException(err);
                }
                toast.error(translateAuthMessage(t, mnemonic?.message, 'auth.register.error'));
        }
    };

    const onSubmitForm: SubmitHandler<FormValues> = async (formData) => {
        try {
            const result = await storefrontAuth.registerRequest(formData.phone);

            setPhone(formData.phone);
            setPassword(formData.password);
            setSecondsLeft(result.seconds_remaining ?? RESEND_TIMEOUT_SECONDS);
            setStep('code');
        } catch (err) {
            handleRegisterRequestError(err);
        }
    };

    const onSubmitCode: SubmitHandler<CodeValues> = async (codeData) => {
        try {
            const tokens = await storefrontAuth.registerComplete({
                phone,
                password,
                code: codeData.code,
                referralCode,
            });
            if (tokens.access_token && tokens.refresh_token) {
                await setSession({ access_token: tokens.access_token, refresh_token: tokens.refresh_token });
                reachGoal(region.code, ANALYTICS_GOALS.REGISTRATION);
            }
            setStep('success');
        } catch (err) {
            const mnemonic = getAuthMnemonicFromError(err);
            if (!mnemonic || mnemonic.message === 'auth.internal_error') {
                Sentry.captureException(err);
            }
            toast.error(translateAuthMessage(t, mnemonic?.message, 'auth.register.code.error'));
        }
    };

    const handleResend = async (): Promise<void> => {
        setIsResending(true);
        try {
            const result = await storefrontAuth.registerRequest(phone);
            setSecondsLeft(result.seconds_remaining ?? RESEND_TIMEOUT_SECONDS);
        } catch (err) {
            handleRegisterRequestError(err);
        } finally {
            setIsResending(false);
        }
    };

    const handleOpenLogin = (): void => {
        openModal<LoginModalData>({ ModalComponent: LoginModal, data: { referralCode } });
        onClose();
    };

    if (step === 'success') {
        return (
            <AuthModalLayout onClose={onClose} zIndex={zIndex} title={t('auth.register.success.title')}>
                <div className={styles['success-content']}>
                    <Icon icon={CheckCircleIcon} size="5xl" color="success" />

                    <Text align="center" size="md" lineHeight="relaxed">
                        {t('auth.register.success.message')}
                    </Text>

                    <Button className="mt-4" onClick={onClose}>
                        {t('auth.register.success.close')}
                    </Button>
                </div>
            </AuthModalLayout>
        );
    }

    if (step === 'code') {
        return (
            <AuthModalLayout onClose={onClose} zIndex={zIndex} title={t('auth.register.code.title')}>
                <Text align="center" size="md" className="mb-2">
                    {t('auth.register.code.sent-to', { phone })}
                </Text>

                <form
                    className={classNames(styles.form, 'mt-4')}
                    onSubmit={(event) => void handleCodeSubmit(onSubmitCode)(event)}
                >
                    <FormGroup label={t('auth.register.code.label')} errorText={codeErrors.code?.message}>
                        <Input
                            {...registerCode('code', {
                                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                                    e.target.value = e.target.value.replace(/\D/g, '');
                                },
                            })}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder={t('auth.register.code.placeholder')}
                            error={Boolean(codeErrors.code)}
                        />
                    </FormGroup>

                    <Button className="mb-2" type="submit" size="lg" disabled={isCodeSubmitting}>
                        {t('auth.register.code.submit')}
                    </Button>

                    <Text align="center" color="muted" className={styles.resend}>
                        {secondsLeft > 0 ? (
                            t('auth.register.code.resend-timer', { time: formatCountdown(secondsLeft) })
                        ) : (
                            <button
                                type="button"
                                className={styles.link}
                                disabled={isResending}
                                onClick={() => void handleResend()}
                            >
                                {t('auth.register.code.resend')}
                            </button>
                        )}
                    </Text>
                </form>
            </AuthModalLayout>
        );
    }

    return (
        <AuthModalLayout onClose={onClose} zIndex={zIndex} title={t('auth.register.title')}>
            <Text align="center" size="md" className="mb-2">
                {t('auth.register.has-account')}{' '}
                <button type="button" className={styles.link} onClick={handleOpenLogin}>
                    {t('auth.register.sign-in')}
                </button>
            </Text>

            {referralCode && (
                <div className={classNames(styles.referral, 'mt-3')}>
                    <Icon icon={GiftIcon} size="xl" color="primary" />

                    <div>
                        <Text size="sm" weight="medium">
                            {t('auth.register.referral.label')}
                        </Text>

                        <Text size="sm" color="muted">
                            {t('auth.register.referral.code', { code: referralCode })}
                        </Text>
                    </div>
                </div>
            )}

            <form
                className={classNames(styles.form, 'mt-4')}
                onSubmit={(event) => void handleSubmit(onSubmitForm)(event)}
            >
                <FormGroup required label={t('auth.field.phone.label')} errorText={errors.phone?.message}>
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <PhoneInput
                                value={field.value}
                                onChange={field.onChange}
                                defaultCountry={country.code.toLowerCase() as CountryIso2}
                                locale={i18n.language}
                                countryButtonLabel={t('form.phone.country-button')}
                                countryListLabel={t('form.phone.country-list')}
                                error={Boolean(errors.phone)}
                                placeholder={t('auth.field.phone.placeholder')}
                            />
                        )}
                    />
                </FormGroup>

                <FormGroup required label={t('auth.field.password.label')} errorText={errors.password?.message}>
                    <PasswordInput
                        {...register('password')}
                        showPasswordLabel={t('form.password.show')}
                        hidePasswordLabel={t('form.password.hide')}
                        placeholder={t('auth.field.password.placeholder')}
                        error={Boolean(errors.password)}
                    />
                </FormGroup>

                <FormGroup
                    required
                    label={t('auth.field.confirm-password.label')}
                    errorText={errors.confirmPassword?.message}
                >
                    <PasswordInput
                        {...register('confirmPassword')}
                        showPasswordLabel={t('form.password.show')}
                        hidePasswordLabel={t('form.password.hide')}
                        placeholder={t('auth.field.confirm-password.placeholder')}
                        error={Boolean(errors.confirmPassword)}
                    />
                </FormGroup>

                <Button className="mt-4" type="submit" size="lg" disabled={isSubmitting}>
                    {t('auth.register.submit')}
                </Button>
            </form>
        </AuthModalLayout>
    );
}
